import axios from 'axios';

/**
 * Helper function to get auth headers for API calls
 * FIXED: Token is stored separately from userInfo in localStorage
 */
function authHeaders(extra = {}) {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    // Desktop: the sidecar verifies this cloud JWT against
    // GET /auth/entitlement — no local identity, no free-forever desktop.
    // Cloud ignores the extra header, so it's safe to send always.
    'X-Cloud-Token': token || '',
    'user_email': userInfo?.email || '',
    ...extra
  };
}

let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export function setApiBase(url) { API_BASE_URL = url; }

export class DataService {
  // UPDATED: Added projectId parameter
  static async loadData(file, fileType, page = 1, pageSize = 100, projectId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('page', page.toString());
      formData.append('page_size', pageSize.toString());
      
      // CRITICAL: Add project_id so data is saved to database
      console.log('='.repeat(60));
      console.log('DataService.loadData called');
      console.log('  File:', file?.name);
      console.log('  File type:', fileType);
      console.log('  Project ID received:', projectId);
      
      if (projectId) {
        formData.append('project_id', projectId);
        console.log('  ✓ project_id added to FormData:', projectId);
      } else {
        console.warn('  ⚠ WARNING: No projectId provided - data will NOT persist!');
      }
      console.log('='.repeat(60));

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      console.log('User info from localStorage:', userInfo);

      const response = await fetch(`${API_BASE_URL}/load-data/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
        },
        body: formData
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to load data';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Loaded data:', data);
      return data;
    } catch (error) {
      console.error('DataService error:', error);
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please ensure the backend server is running and accessible.');
      }
      throw error;
    }
  }

  // Method for loading BACKCHECK data (saves as BACKCHECK type in database)
  static async loadBackcheckData(file, fileType, page = 1, pageSize = 100, projectId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('page', page.toString());
      formData.append('page_size', pageSize.toString());
      formData.append('data_file_type', 'backcheck');  // Tell backend this is backcheck data
      
      console.log('='.repeat(60));
      console.log('DataService.loadBackcheckData called');
      console.log('  File:', file?.name);
      console.log('  File type:', fileType);
      console.log('  Project ID received:', projectId);
      
      if (projectId) {
        formData.append('project_id', projectId);
        console.log('  ✓ project_id added to FormData:', projectId);
      } else {
        console.warn('  ⚠ WARNING: No projectId provided - backcheck data will NOT persist!');
      }
      console.log('='.repeat(60));

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      const response = await fetch(`${API_BASE_URL}/load-data/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
        },
        body: formData
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to load backcheck data';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Loaded backcheck data:', data);
      return data;
    } catch (error) {
      console.error('DataService backcheck error:', error);
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please ensure the backend server is running and accessible.');
      }
      throw error;
    }
  }

  static async getPostgresTables(config) {
    try {
      const { host, port, database, user, password } = config;
      const queryParams = new URLSearchParams({
        host,
        port: port.toString(),
        database,
        user,
        password
      });

      const response = await fetch(`${API_BASE_URL}/postgres-tables/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch PostgreSQL tables');
      }

      const data = await response.json();
      return data.tables;
    } catch (error) {
      console.error('PostgreSQL tables fetch error:', error);
      throw error;
    }
  }

  static async getGoogleSheets(config) {
    try {
      const response = await fetch(`${API_BASE_URL}/google-sheets/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Google Sheets');
      }

      const data = await response.json();
      return data.sheets;
    } catch (error) {
      console.error('Google Sheets fetch error:', error);
      throw error;
    }
  }

  static async loadPostgresData(postgresConfig, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-postgres/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...postgresConfig,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load PostgreSQL data');
      }

      return await response.json();
    } catch (error) {
      console.error('PostgreSQL load error:', error);
      throw error;
    }
  }

  static async getMysqlTables(config) {
    try {
      const { host, port, database, user, password } = config;
      const queryParams = new URLSearchParams({
        host,
        port: port.toString(),
        database,
        user,
        password
      });

      const response = await fetch(`${API_BASE_URL}/mysql-tables/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch MySQL tables');
      }

      const data = await response.json();
      return data.tables;
    } catch (error) {
      console.error('MySQL tables fetch error:', error);
      throw error;
    }
  }

  static async loadMysqlData(mysqlConfig, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-mysql/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...mysqlConfig,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load MySQL data');
      }

      return await response.json();
    } catch (error) {
      console.error('MySQL load error:', error);
      throw error;
    }
  }

  static async loadMariadbData(mariadbConfig, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-mariadb/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...mariadbConfig,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load MariaDB data');
      }

      return await response.json();
    } catch (error) {
      console.error('MariaDB load error:', error);
      throw error;
    }
  }

  static async loadMongoData(mongoConfig, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-mongodb/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...mongoConfig,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load MongoDB data');
      }

      return await response.json();
    } catch (error) {
      console.error('MongoDB load error:', error);
      throw error;
    }
  }

  static async loadDynamoData(dynamoConfig, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-dynamodb/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...dynamoConfig,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load DynamoDB data');
      }

      return await response.json();
    } catch (error) {
      console.error('DynamoDB load error:', error);
      throw error;
    }
  }

  /**
   * Get list of MongoDB collections
   */
  static async getMongoCollections(config) {
    try {
      const { host, port, database, username, password } = config;
      const queryParams = new URLSearchParams({
        host,
        port: port.toString(),
        database,
        ...(username && { username }),
        ...(password && { password })
      });

      const response = await fetch(`${API_BASE_URL}/mongo-collections/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch MongoDB collections');
      }

      const data = await response.json();
      return data.collections;
    } catch (error) {
      console.error('MongoDB collections fetch error:', error);
      throw error;
    }
  }

  /**
   * Get list of DynamoDB tables
   */
  static async getDynamoTables(config) {
    try {
      const { region, access_key_id, secret_access_key } = config;
      const queryParams = new URLSearchParams({
        region,
        access_key_id,
        secret_access_key
      });

      const response = await fetch(`${API_BASE_URL}/dynamo-tables/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch DynamoDB tables');
      }

      const data = await response.json();
      return data.tables;
    } catch (error) {
      console.error('DynamoDB tables fetch error:', error);
      throw error;
    }
  }

  static async loadSqliteTable(file, tableName) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('table_name', tableName);

      const response = await fetch(`${API_BASE_URL}/load-sqlite-table/`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load SQLite table');
      }

      return await response.json();
    } catch (error) {
      console.error('SQLite table load error:', error);
      throw error;
    }
  }

  static async loadOdkData(config, projectId = null, dataFileType = 'primary') {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/load-odk/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          server_url: config.server_url.trim(),
          email: config.email.trim(),
          password: config.password,
          odk_project_id: parseInt(config.odk_project_id),
          form_id: config.form_id.trim(),
          project_id: projectId,
          data_file_type: dataFileType
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to load ODK Central data';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from server');
      }

      return data;
    } catch (error) {
      console.error('ODK Central load error:', error);
      
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      } else if (error.message.includes('401') || error.message.includes('credentials')) {
        throw new Error('Authentication failed. Please check your ODK Central email and password.');
      } else if (error.message.includes('404')) {
        throw new Error('Form or project not found. Please verify the Project ID and Form ID.');
      } else if (error.message.includes('403')) {
        throw new Error('Access denied. Check your permissions for this project/form.');
      }
      
      throw error;
    }
  }

  static async loadSurveyCTOData(config, projectId = null, dataFileType = 'primary') {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/load-surveycto/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          server_url: config.server_url.trim(),
          username: config.username.trim(),
          password: config.password,
          form_id: config.form_id.trim(),
          project_id: projectId,
          data_file_type: dataFileType
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to load SurveyCTO data';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from server');
      }

      return data;
    } catch (error) {
      console.error('SurveyCTO load error:', error);
      
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.message.includes('401') || error.message.includes('Authentication failed')) {
        throw new Error('Authentication failed. Please check your SurveyCTO credentials.');
      } else if (error.message.includes('404') || error.message.includes('Form not found')) {
        throw new Error('Form not found. Please verify the Form ID is correct.');
      } else if (error.message.includes('403') || error.message.includes('Access forbidden')) {
        throw new Error('Access forbidden. You may not have permission to access this form.');
      } else if (error.message.includes('500')) {
        throw new Error('Server error occurred. Please try again later.');
      }
      
      throw error;
    }
  }

  static async getSurveyCTOForms(config) {
    try {
      const response = await fetch(`${API_BASE_URL}/surveycto-forms/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          server_url: config.server_url.trim(),
          username: config.username.trim(),
          password: config.password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch SurveyCTO forms');
      }

      const data = await response.json();
      return data.forms || [];
    } catch (error) {
      console.error('SurveyCTO forms fetch error:', error);
      throw error;
    }
  }

  static async getSurveyCTODatasets(config) {
    try {
      const response = await fetch(`${API_BASE_URL}/surveycto-datasets/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          server_url: config.server_url.trim(),
          username: config.username.trim(),
          password: config.password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch SurveyCTO datasets');
      }

      const data = await response.json();
      return data.datasets || [];
    } catch (error) {
      console.error('SurveyCTO datasets fetch error:', error);
      throw error;
    }
  }

  static async validateSurveyCTOConnection(config) {
    try {
      const forms = await this.getSurveyCTOForms(config);
      return forms;
    } catch (error) {
      console.error('SurveyCTO connection validation error:', error);
      throw error;
    }
  }

  static async loadGoogleSheetsData(config, page = 1, pageSize = 100) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const response = await fetch(`${API_BASE_URL}/load-googlesheets/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...config,
          page,
          page_size: pageSize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load Google Sheets data');
      }

      return await response.json();
    } catch (error) {
      console.error('Google Sheets load error:', error);
      throw error;
    }
  }

  static async getKoboAssets(config) {
    try {
      const { server_url, username, password } = config;
      const credentials = btoa(`${username}:${password}`);
      
      const response = await fetch(`${server_url}/api/v2/assets/`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch KoBoToolbox assets: ${response.status}`);
      }

      const data = await response.json();
      
      const assets = data.results.filter(asset => 
        asset.asset_type === 'survey' && asset.has_deployment
      ).map(asset => ({
        uid: asset.uid,
        name: asset.name,
        date_created: asset.date_created,
        date_modified: asset.date_modified,
        deployment_active: asset.deployment__active
      }));

      return assets;
    } catch (error) {
      console.error('KoBoToolbox assets fetch error:', error);
      throw error;
    }
  }

  static async getKoboExportSettings(config) {
    try {
      const { server_url, username, password, asset_uid } = config;
      
      if (!asset_uid) {
        throw new Error('Asset UID is required to fetch export settings');
      }
      
      const credentials = btoa(`${username}:${password}`);
      
      const response = await fetch(`${server_url}/api/v2/assets/${asset_uid}/export-settings/`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch export settings: ${response.status}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('KoBoToolbox export settings fetch error:', error);
      throw error;
    }
  }

  static async loadKoboData(config, projectId = null, dataFileType = 'primary') {
    try {
      const { server_url, username, password, asset_uid, export_setting_id, sheet_name } = config;
      
      if (!asset_uid) {
        throw new Error('Asset UID is required');
      }
      
      let exportSettingId = export_setting_id;
      if (!exportSettingId) {
        const exportSettings = await this.getKoboExportSettings({
          server_url, username, password, asset_uid
        });
        
        if (exportSettings.length === 0) {
          throw new Error('No export settings found for this asset');
        }
        
        exportSettingId = exportSettings[0].uid;
      }
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/load-kobo/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'user_email': userInfo?.email || '',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          server_url,
          username,
          password,
          asset_uid,
          export_setting_id: exportSettingId,
          sheet_name: sheet_name || null,
          project_id: projectId,
          data_file_type: dataFileType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load KoBoToolbox data');
      }

      return await response.json();
    } catch (error) {
      console.error('KoBoToolbox load error:', error);
      throw error;
    }
  }

  // ============================================================================
  // API CONNECTION CONFIG METHODS
  // ============================================================================

  /**
   * Save API connection configuration for a project
   * @param {string} projectId - Project UUID
   * @param {string} dataType - 'primary' or 'backcheck'
   * @param {object} config - { connection_type, settings, auto_refresh }
   */
  static async saveAPIConfig(projectId, dataType, config) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/api-config/${dataType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        },
        body: JSON.stringify(config)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save API configuration');
      }
      
      console.log(`API config saved for project ${projectId}, type: ${dataType}`);
      return await response.json();
    } catch (error) {
      console.error('Error saving API config:', error);
      throw error;
    }
  }

  /**
   * Get saved API connection configuration for a project
   * @param {string} projectId - Project UUID
   * @param {string} dataType - 'primary' or 'backcheck'
   */
  static async getAPIConfig(projectId, dataType) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/api-config/${dataType}`, {
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        }
      });
      
      // 404 is a normal answer here: no API source configured for this
      // project. Return null quietly instead of throwing and logging red.
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get API configuration');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting API config:', error);
      throw error;
    }
  }

  /**
   * Refresh data using stored API credentials
   * @param {string} projectId - Project UUID
   * @param {string} dataType - 'primary' or 'backcheck'
   */
  static async refreshAPIData(projectId, dataType) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/refresh-api-data/${dataType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to refresh data from API');
      }
      
      console.log(`Data refreshed for project ${projectId}, type: ${dataType}`);
      return await response.json();
    } catch (error) {
      console.error('Error refreshing API data:', error);
      throw error;
    }
  }

  /**
   * Update refresh schedule for an API connection
   * @param {string} projectId - Project UUID
   * @param {string} dataType - 'primary' or 'backcheck'
   * @param {string} refreshInterval - 'manual', 'hourly', 'daily', 'weekly'
   */
  static async updateRefreshSchedule(projectId, dataType, refreshInterval) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/api-config/${dataType}/schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        },
        body: JSON.stringify({ refresh_interval: refreshInterval })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update refresh schedule');
      }
      
      console.log(`Refresh schedule updated for project ${projectId}: ${refreshInterval}`);
      return await response.json();
    } catch (error) {
      console.error('Error updating refresh schedule:', error);
      throw error;
    }
  }

  /**
   * Disconnect data source - removes saved API/database configuration
   */
  static async disconnectDataSource(projectId, dataType) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/api-config/${dataType}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to disconnect data source');
      }
      
      console.log(`Data source disconnected for project ${projectId} (${dataType})`);
      return await response.json();
    } catch (error) {
      console.error('Error disconnecting data source:', error);
      throw error;
    }
  }
  
  /**
   * Get scheduler status
   */
  static async getSchedulerStatus() {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      
      const response = await fetch(`${API_BASE_URL}/api/scheduler/status`, {
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || ''
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get scheduler status');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting scheduler status:', error);
      throw error;
    }
  }

  // ============================================================================
  // PAGINATED DATA ACCESS (Phase 1 Performance Optimization)
  // ============================================================================

  /**
   * Fetch paginated project data.
   * Replaces full_data transfers — call this for DataPreview, tables, etc.
   */
  static async getProjectData(projectId, options = {}) {
    try {
      const { page = 1, pageSize = 50, fileType = 'primary', search = null, sortBy = null, sortOrder = 'asc' } = options;
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString(), file_type: fileType });
      if (search) params.append('search', search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/data?${params}`, {
        headers: authHeaders({ 'Accept': 'application/json' }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch project data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching paginated data:', error);
      throw error;
    }
  }

  /**
   * Fetch full project data (for exports, cleaning scripts, etc.)
   * Use sparingly — prefer getProjectData() for display.
   */
  static async getProjectDataFull(projectId, fileType = 'primary') {
    try {
      const params = new URLSearchParams({ file_type: fileType });
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/data/full?${params}`, {
        headers: authHeaders({ 'Accept': 'application/json' }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch full project data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching full data:', error);
      throw error;
    }
  }

  // ============================================================================
  // AUDIO AUDIT SETTINGS — per-project media folder + audio column metadata
  // ============================================================================

  /**
   * Fetch saved media settings for a project.
   * Returns null if no settings have been set yet (HTTP 404).
   * Settings shape: { media_dir: string, audio_columns?: string[], audio_language?: string, updated_at?: string }
   *
   * Backend reads from new `media_settings` key, falling back to the legacy
   * `audio_settings` key for projects that pre-date the rename.
   */
  static async getMediaSettings(projectId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/media-settings`,
        { headers: authHeaders({ 'Accept': 'application/json' }) }
      );
      if (response.status === 404) return null; // not yet set (older backend)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch media settings');
      }
      const data = await response.json();
      if (data && data.exists === false) return null; // not yet set (new backend)
      return data;
    } catch (error) {
      console.error('Error fetching media settings:', error);
      throw error;
    }
  }

  /**
   * Save media settings for a project.
   * settings shape: { media_dir: string, audio_columns?: string[], audio_language?: string }
   */
  static async saveMediaSettings(projectId, settings) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/media-settings`,
        {
          method: 'PUT',
          headers: authHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }),
          body: JSON.stringify(settings),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save media settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving media settings:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getMediaSettings — kept as an alias so old call sites
   * continue to work. The setting is project-wide (not audio-specific).
   */
  static async getAudioSettings(projectId) {
    return this.getMediaSettings(projectId);
  }

  /**
   * @deprecated Use saveMediaSettings — kept as an alias so old call sites
   * continue to work. The setting is project-wide (not audio-specific).
   */
  static async saveAudioSettings(projectId, settings) {
    return this.saveMediaSettings(projectId, settings);
  }

  // ============================================================================
  // AUDIO AUDIT — REVIEWER NOTES & DISPOSITIONS
  // ============================================================================

  /**
   * Fetch the project's audio notes document.
   * Returns the full { notes, dispositions, updated_at } shape, or null if
   * none has been saved yet.
   */
  static async getAudioNotes(projectId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/audio-notes`,
        { headers: authHeaders({ 'Accept': 'application/json' }) }
      );
      if (response.status === 404) return null; // older backend
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch audio notes');
      }
      const data = await response.json();
      if (data && data.exists === false) return null; // not yet set (new backend)
      return data;
    } catch (error) {
      console.error('Error fetching audio notes:', error);
      throw error;
    }
  }

  /**
   * Save the entire audio notes document (whole-document replacement).
   * Frontend mutates locally then PUTs the updated payload — same pattern
   * used by audio_settings, cleaning_state, etc.
   */
  static async saveAudioNotes(projectId, payload) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/audio-notes`,
        {
          method: 'PUT',
          headers: authHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save audio notes');
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving audio notes:', error);
      throw error;
    }
  }

  // ============================================================================
  // TRANSCRIPTS META — confidence + segment metadata from Whisper sidecar
  // ============================================================================

  /**
   * Fetch the project's transcripts meta document.
   * Returns the full payload, or null if none has been uploaded yet.
   */
  static async getTranscriptsMeta(projectId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/transcripts-meta`,
        { headers: authHeaders({ 'Accept': 'application/json' }) }
      );
      if (response.status === 404) return null; // older backend
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch transcripts meta');
      }
      const data = await response.json();
      if (data && data.exists === false) return null; // not yet set (new backend)
      return data;
    } catch (error) {
      console.error('Error fetching transcripts meta:', error);
      throw error;
    }
  }

  /**
   * Save the transcripts meta document (whole-document replacement).
   * Called by TranscribeModal after Apply-to-Project succeeds.
   */
  static async saveTranscriptsMeta(projectId, payload) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/transcripts-meta`,
        {
          method: 'PUT',
          headers: authHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save transcripts meta');
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving transcripts meta:', error);
      throw error;
    }
  }

  // ============================================================================
  // SERVER-SIDE QUALITY CHECKS (Phase 2 Performance Optimization)
  // ============================================================================

  /**
   * Run quality checks server-side by project ID.
   * No raw data is sent — the backend loads data from the database.
   */
  static async runQualityChecksV2(checks) {
    try {
      const response = await fetch(`${API_BASE_URL}/run-quality-checks-v2`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' }),
        body: JSON.stringify(checks),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to run quality checks');
      }
      return await response.json();
    } catch (error) {
      console.error('Error running quality checks v2:', error);
      throw error;
    }
  }

  /**
   * Run backcheck analysis server-side by project ID.
   */
  static async runBackcheckAnalysisV2(projectId, config) {
    try {
      const response = await fetch(`${API_BASE_URL}/run-backcheck-analysis-v2`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' }),
        body: JSON.stringify({ project_id: projectId, config }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to run backcheck analysis');
      }
      return await response.json();
    } catch (error) {
      console.error('Error running backcheck analysis v2:', error);
      throw error;
    }
  }
}